import React, { useCallback, useEffect, useState } from 'react';
import {
  CreditCard, BarChart2, Layers, Search, Filter, RefreshCw,
  ChevronLeft, ChevronRight, MoreVertical, Clock, FileText,
} from 'lucide-react';
import { useApi } from '../../lib/api';

const TYPES = ['payment', 'usage'];
const SEVERITIES = ['info', 'warning', 'error', 'critical'];
const PAGE_SIZE = 10;

const TYPE_ICONS = {
  payment: CreditCard,
  usage:   BarChart2,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 23) + (iso.endsWith('Z') ? 'Z' : '');
}

export default function ReconciliationPage() {
  const api = useApi();
  const [filter, setFilter]   = useState({ type: '', status: 'open', severity: '' });
  const [findings, setFindings] = useState([]);
  const [runs, setRuns]       = useState([]);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState({});
  const [selected, setSelected] = useState(null);
  const [note, setNote]       = useState('');
  const [runsPage, setRunsPage] = useState(1);
  const [searchRuns, setSearchRuns] = useState('');
  const [openMenu, setOpenMenu] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        api.adminListFindings({
          status:   filter.status,
          severity: filter.severity || undefined,
          job_type: filter.type || undefined,
          limit: 200,
        }),
        api.adminListRuns({ type: filter.type || undefined, limit: 100 }),
      ]);
      setFindings(f.findings || []);
      setRuns(r.runs || []);
      setRunsPage(1);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  // close menu on outside click
  useEffect(() => {
    const h = () => setOpenMenu(null);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const triggerRun = async (type) => {
    setBusy((b) => ({ ...b, [type]: true }));
    try {
      await api.adminTriggerRun(type);
      await load();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setBusy((b) => ({ ...b, [type]: false }));
    }
  };

  const resolve = async () => {
    if (!selected) return;
    if (!note.trim()) { alert('Please write a resolution note'); return; }
    try {
      await api.adminResolveFinding(selected.id, note.trim());
      setSelected(null); setNote('');
      load();
    } catch (err) {
      alert(err?.response?.data?.error || err.message);
    }
  };

  if (error) return <div className="admin-banner error">{error}</div>;

  // Runs pagination + search
  const filteredRuns = runs.filter(r =>
    !searchRuns ||
    r.job_type?.includes(searchRuns.toLowerCase()) ||
    String(r.id).includes(searchRuns) ||
    r.status?.includes(searchRuns.toLowerCase()) ||
    r.triggered_by?.includes(searchRuns.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredRuns.length / PAGE_SIZE));
  const pagedRuns  = filteredRuns.slice((runsPage - 1) * PAGE_SIZE, runsPage * PAGE_SIZE);

  // Page number display
  const pageNums = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) pageNums.push(i);
  } else {
    pageNums.push(1, 2, 3);
    if (runsPage > 3 && runsPage < totalPages - 1) pageNums.push('…', runsPage);
    pageNums.push('…', totalPages);
  }
  const uniquePages = [...new Set(pageNums)];

  return (
    <>
      {/* Header */}
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Reconciliation</h1>
          <p className="admin-page-subtitle">Recent runs + open findings across all jobs.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {TYPES.map((t) => {
            const Icon = TYPE_ICONS[t];
            return (
              <button
                key={t}
                className={t === 'payment' ? 'btn-primary' : 'btn-secondary'}
                disabled={busy[t]}
                onClick={() => triggerRun(t)}
              >
                <Icon size={13} />
                {busy[t] ? 'Running…' : `Run ${t}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent Runs card */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={16} color="#6b7280" />
            <h2 style={{ margin: 0 }}>Recent runs</h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                placeholder="Search runs..."
                value={searchRuns}
                onChange={e => { setSearchRuns(e.target.value); setRunsPage(1); }}
                autoComplete="off"
                style={{
                  font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px',
                  border: '1px solid #d1d5db', borderRadius: 8, width: 180,
                  outline: 'none', color: '#374151',
                }}
              />
            </div>
            {/* Filter type */}
            <div style={{ position: 'relative' }}>
              <Filter size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <select
                value={filter.type}
                onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
                style={{
                  font: 'inherit', fontSize: 13, padding: '6px 28px 6px 30px',
                  border: '1px solid #d1d5db', borderRadius: 8,
                  appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
                  color: '#374151', cursor: 'pointer',
                }}
              >
                <option value="">All types</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button onClick={load} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }}>
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {runs.length === 0 ? (
          <div className="admin-empty">No runs yet.</div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>SL. NO</th>
                  <th>TYPE</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }}>STARTED ↓</th>
                  <th>FINISHED</th>
                  <th>STATUS</th>
                  <th>SCANNED</th>
                  <th>FINDINGS</th>
                  <th>TRIGGER</th>
                  <th style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {pagedRuns.map((r) => (
                  <tr key={r.id}>
                    <td style={{ color: '#6b7280', fontSize: 12 }}>{r.id}</td>
                    <td style={{ fontWeight: 500, color: '#374151' }}>{r.job_type}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>{fmtDate(r.started_at)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>{fmtDate(r.finished_at)}</td>
                    <td>
                      <span className={`admin-status-pill ${r.status === 'succeeded' ? 'succeeded' : r.status === 'running' ? 'running' : 'failed'}`}>
                        {r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : '—'}
                      </span>
                    </td>
                    <td>{r.scanned_count}</td>
                    <td>{r.finding_count}</td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>{r.triggered_by}</td>
                    <td>
                      <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setOpenMenu(openMenu === r.id ? null : r.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: '#9ca3af' }}
                        >
                          <MoreVertical size={15} />
                        </button>
                        {openMenu === r.id && (
                          <div style={{
                            position: 'absolute', right: 0, top: '100%', zIndex: 50,
                            background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: 140, overflow: 'hidden',
                          }}>
                            <button
                              onClick={() => { setOpenMenu(null); }}
                              style={{ display: 'block', width: '100%', padding: '8px 14px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, cursor: 'pointer', color: '#374151' }}
                            >
                              View details
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                Showing {Math.min((runsPage - 1) * PAGE_SIZE + 1, filteredRuns.length)} to {Math.min(runsPage * PAGE_SIZE, filteredRuns.length)} of {filteredRuns.length} runs
              </span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  onClick={() => setRunsPage(p => Math.max(1, p - 1))}
                  disabled={runsPage === 1}
                  style={{ width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: runsPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: runsPage === 1 ? '#d1d5db' : '#374151' }}
                >
                  <ChevronLeft size={14} />
                </button>
                {uniquePages.map((p, i) =>
                  p === '…' ? (
                    <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setRunsPage(p)}
                      style={{
                        width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
                        background: runsPage === p ? '#0f1c2e' : 'white',
                        color: runsPage === p ? 'white' : '#374151',
                        cursor: 'pointer', fontSize: 13, fontWeight: runsPage === p ? 700 : 400,
                      }}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setRunsPage(p => Math.min(totalPages, p + 1))}
                  disabled={runsPage === totalPages}
                  style={{ width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: runsPage === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: runsPage === totalPages ? '#d1d5db' : '#374151' }}
                >
                  <ChevronRight size={14} />
                </button>
                {/* Per page */}
                <select
                  style={{
                    font: 'inherit', fontSize: 13, padding: '5px 24px 5px 8px',
                    border: '1px solid #e5e7eb', borderRadius: 6,
                    appearance: 'none', background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 6px center`,
                    color: '#374151', cursor: 'pointer', marginLeft: 4,
                  }}
                  defaultValue="10"
                  disabled
                >
                  <option value="10">10 / page</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Findings card */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} color="#6b7280" />
            <h2 style={{ margin: 0 }}>Findings</h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Status */}
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <select
                value={filter.status}
                onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
                style={filterSelectStyle}
              >
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            {/* Type */}
            <div style={{ position: 'relative' }}>
              <Layers size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <select
                value={filter.type}
                onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
                style={filterSelectStyle}
              >
                <option value="">All types</option>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {/* Severity */}
            <div style={{ position: 'relative' }}>
              <BarChart2 size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <select
                value={filter.severity}
                onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}
                style={filterSelectStyle}
              >
                <option value="">All severities</option>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
              {findings.length} finding(s)
            </span>
          </div>
        </div>

        {findings.length === 0 ? (
          <div className="admin-empty">
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            No findings match.
          </div>
        ) : (
          <div className="admin-table-scroll"><table className="admin-table">
            <thead>
              <tr>
                <th>SL. NO</th><th>SEVERITY</th><th>TYPE</th><th>CODE</th>
                <th>APP</th><th>CREATED</th><th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f) => (
                <tr key={f.id} className="clickable" onClick={() => setSelected(f)}>
                  <td style={{ color: '#6b7280', fontSize: 12 }}>{f.id}</td>
                  <td><span className={`sev ${f.severity}`}>{f.severity}</span></td>
                  <td style={{ fontWeight: 500 }}>{f.job_type}</td>
                  <td><code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{f.code}</code></td>
                  <td>{f.app_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#6b7280' }}>{fmtDate(f.created_at)}</td>
                  <td>
                    {f.resolved_at
                      ? <span className="admin-status-pill ok">Resolved</span>
                      : <span className="admin-status-pill partial">Open</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Finding detail modal */}
      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal-panel modal-panel--lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Finding #{selected.id}</h2>
              <button className="modal-close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                <span className={`sev ${selected.severity}`}>{selected.severity}</span>
                <code style={{ fontSize: 13, background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>{selected.code}</code>
                {selected.app_name && <span style={{ color: '#6b7280', fontSize: 13 }}>· {selected.app_name}</span>}
              </div>
              <p style={{ color: '#9ca3af', fontSize: 12, margin: '0 0 16px' }}>
                Created {fmtDate(selected.created_at)}
                {selected.resolved_at && <> · Resolved {fmtDate(selected.resolved_at)}</>}
              </p>
              <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: '#374151' }}>Detail</h3>
              <pre style={{ background: '#f9fafb', border: '1px solid #e5e7eb', padding: 12, borderRadius: 8, fontSize: 12, overflow: 'auto', maxHeight: 240, color: '#374151' }}>
                {pretty(selected.detail_json)}
              </pre>
              {selected.resolution_note && (
                <>
                  <h3 style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 6px', color: '#374151' }}>Resolution note</h3>
                  <p style={{ color: '#374151', fontSize: 13 }}>{selected.resolution_note}</p>
                </>
              )}
              {!selected.resolved_at && (
                <>
                  <h3 style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 6px', color: '#374151' }}>Resolve</h3>
                  <textarea
                    rows={3}
                    placeholder="Describe what you did to resolve…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ width: '100%', font: 'inherit', fontSize: 13, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <button className="btn-primary" style={{ marginTop: 8 }} onClick={resolve}>Mark resolved</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const filterSelectStyle = {
  font: 'inherit', fontSize: 13, padding: '6px 28px 6px 30px',
  border: '1px solid #d1d5db', borderRadius: 8,
  appearance: 'none',
  background: `white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center`,
  color: '#374151', cursor: 'pointer',
};

function pretty(s) {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}
